import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'

import { useAuthStore } from 'src/stores/auth'
import { ME_CACHE_TTL_MS, authService } from 'src/services/api/auth.service'
import { QUERY_KEYS } from 'src/services/consts/queries.const'
import { usePostHog } from 'src/composables/app/ui/usePostHog'
import { broadcastAccountData } from 'src/composables/app/system/useBroadcastChannel'
import { refetchEnabledWhenVisible } from 'src/composables/app/system/useOnlineStatus'

let identifiedUserId = ''

/**
 * Single hydration composable for the Cookie Armor shell.
 * Backed by GET /me — the first authenticated API call after Auth0 resolves.
 *
 * Gated on Auth0 session readiness. Seeds Vue Query from `authService.getMeAccountCache`
 * via `placeholderData` while the network fetch resolves.
 */
export function useAccountQuery() {
  const authStore = useAuthStore()
  const { identify } = usePostHog()

  const enableQuery = computed(() => authStore.isAuthenticated && !authStore.isLoading)

  const accountDataQuery = useQuery({
    queryKey: [QUERY_KEYS.ACCOUNT_DATA],
    staleTime: ME_CACHE_TTL_MS, // 8 hours to correlate with ME_ACCOUNT_CACHE TTL in authService.fetchMeAccount
    gcTime: ME_CACHE_TTL_MS, // 8 hours to correlate with ME_ACCOUNT_CACHE TTL in authService.fetchMeAccount
    enabled: enableQuery,
    refetchOnWindowFocus: enableQuery,
    refetchOnReconnect: refetchEnabledWhenVisible(enableQuery),
    placeholderData: () => {
      if (!enableQuery.value) return undefined
      return authService.getMeAccountCache({
        sessionEmail: authStore.auth0User?.email,
      })
    },
    queryFn: async () => {
      const data = await authService.fetchMeAccount()

      if (data?.userId && identifiedUserId !== data.userId) {
        const displayName =
          data.firstName && data.lastName
            ? `${data.firstName} ${data.lastName}`
            : data.firstName || data.lastName || null

        identify(data.userId, {
          accountId: data.accountId || null,
          username: displayName,
          email: data.email || null,
        })
        identifiedUserId = data.userId
      }

      broadcastAccountData(data)
      return data
    },
  })

  const accountData = computed(() => accountDataQuery.data.value || null)
  const accountId = computed(() => accountData.value?.accountId || null)

  const refetch = async () => {
    await accountDataQuery.refetch()
  }

  return {
    accountData,
    accountId,
    isLoading: accountDataQuery.isLoading,
    isRefetching: accountDataQuery.isRefetching,
    error: accountDataQuery.error,
    refetch,
  }
}
